import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import clsx from 'clsx';
import { getCoordinatesForZipCode } from "../../api/location";
import { createPricePrescriptionsFetcher } from "../../api/price-prescriptions";

const useCascadingSelects = (optionGetters, data) => {
  const [values, setValues] = useState(optionGetters.map(_ => ""));

  const options = optionGetters.map((getOptions, index) => [
    { value: "", label: "" },
    ...(index === 0 || values[index - 1]
      ? getOptions(data, ...values) || []
      : [])
  ]);

  const bindings = optionGetters.map((_, index) => ({
    options:  options[index],
    value:    values[index],
    onChange: newValue => {
      const newValues = [...values];
      newValues[index] = newValue;
      newValues.fill("", index + 1);
      setValues(newValues);
    },
  }));

  return [...values, ...bindings];
};

const PricePrescriptionsSearchForm = ({
  collapsed,
  onCollapsedChange,
  onSearchStart,
  onSearch,
  onSearchFailed,
  onValidation,
  onClear,
  components: { Label, Autocomplete, Input, Text, Select, Switch },
  prescription,
  zipCode
}) => {
  const [drugName, setDrugName] = useState(prescription || "");
  const [zip, setZip] = useState(zipCode || "");
  const [drugDetails, setDrugDetails] = useState();

  const [
    form,
    dosage,
    quantity,
    bindFormSelect,
    bindDosageSelect,
    bindQuantitySelect,
  ] = useCascadingSelects(
    [
      data => data?.Forms.map(f => ({ value: f.Form, label: f.Form })),
      (data, form) =>
        data?.Forms.find(f => f.Form === form)?.Strengths.map(s => ({
          value: s.Strength,
          label: s.Strength
        })),
      (data, form, dosage) =>
        data?.Forms.find(f => f.Form === form)
          ?.Strengths.find(s => s.Strength === dosage)
          ?.PackageDescriptions.map(d => ({
            value: d.Value,
            label: d.Value
          }))
    ],
    drugDetails
  );

  const [coords, setCoords] = useState({ latitude: 0, longitude: 0 });
  const [alternatives, setAlternatives] = useState([]);
  const [showAlternatives, setShowAlternatives] = useState(false);
  const fetchPricePrescriptions = useMemo(createPricePrescriptionsFetcher, []);

  useEffect(() => {
    if (zip.length === 5) {
      onValidation({ isValid: true });
      getCoordinatesForZipCode(zip)
        .then(([latitude, longitude]) => setCoords({ latitude, longitude }))
        .catch(() => {});
    }
  }, [zip]);

  useEffect(() => {
    if (drugName && coords.latitude && coords.longitude) {
      if (zip.length === 5) {
        onSearchStart();
        const term = showAlternatives
          ? [drugName, ...alternatives.map(a => a.Name)].join(",")
          : drugName;
        const searchParams = { term, zip, ...coords};
        if (form) searchParams.form = form;
        if (dosage) searchParams.dosage = dosage;
        if (quantity) searchParams.quantity = quantity;
        fetchPricePrescriptions(searchParams)
          .then(res => {
            setDrugDetails(res.data.drugDetails[0]);
            setAlternatives(res.data.alternatives);
            onSearch(res.data.defaultDrugPrices);
          })
          .catch(() => {
            onSearchFailed();
          });
      } else {
        onValidation({
          isValid: false,
          message: "Zip code is required"
        });
      }
    }
  }, [drugName, coords, form, dosage, quantity, showAlternatives]);

  const clearForm = () => {
    setDrugName(prescription);
    setZip(zipCode);
    setDrugDetails(null);
    bindFormSelect.onChange("");
    bindDosageSelect.onChange("");
    bindQuantitySelect.onChange("");
    setShowAlternatives(false);
    onClear();
  };

  const addLabel = (text, child, className) => (
    <div className={`${className}`}>
      <Label text={text} />
      {child}
    </div>
  );

  return (
    <div
      className={`price-prescriptions-search-form-web-sdk ${
        collapsed ? "collapsed-web-sdk" : ""
      }`}
    >
      <div className={clsx(
        collapsed && 'websdk-justify-between',
        !collapsed && 'websdk-justify-end','price-prescriptions-search-form-header-web-sdk')}
      >
        {collapsed ? (
          <div className='price-prescriptions-search-form-header-text-web-sdk'>
            Price and filter
          </div>
        ): null}

        <div
          role='button'
          tabIndex={0}
          onKeyDown={() => onCollapsedChange(!collapsed)}
          className='price-prescriptions-search-form-header-button-web-sdk'
          onClick={() => onCollapsedChange(!collapsed)}
        >
          {collapsed ? "Expand" : "Collapse"}
        </div>
      </div>
      <div className='price-prescriptions-search-form-body-wrapper-web-sdk'>
        <div className='price-prescriptions-search-form-body-web-sdk'>
          <div className='price-prescriptions-search-form-section-web-sdk'>
            {addLabel(
              "Prescriptions",
              <Autocomplete
                name='term'
                suggestions={[]}
                defaultValue={drugName}
                handleChange={({ name }) => setDrugName(name)}
              />,
              "xs:websdk-w-full sm:websdk-w-200px"
            )}
          </div>
          <div className='price-prescriptions-search-form-section-web-sdk'>
            {addLabel(
              "Zip",
              <Input
                name='zip'
                maxLength='5'
                value={zip}
                onChange={e => setZip(e.target.value)}
              />
            )}
          </div>
          {drugDetails && (
            <>
              <div className='price-prescriptions-search-form-section-web-sdk'>
                {addLabel("Form", <Select {...bindFormSelect} />)}
                {addLabel("Dosage", <Select {...bindDosageSelect} />)}
                {addLabel("Quantity", <Select {...bindQuantitySelect} />)}
              </div>
              <div className='price-prescriptions-search-form-section-web-sdk'>
                <Switch
                  name='alternatives'
                  identifier='alternatives'
                  labelText='Include generic, similar'
                  checked={showAlternatives}
                  onChange={e => setShowAlternatives(e.target.checked)}
                />
              </div>
            </>
          )}
        </div>
        <Text label='Clear filters' params='websdk-m-0' onClick={clearForm} />
      </div>
    </div>
  );
};

export default PricePrescriptionsSearchForm;

PricePrescriptionsSearchForm.propTypes = {
  collapsed:         PropTypes.bool,
  components:        PropTypes.object,
  onCollapsedChange: PropTypes.func,
  onSearchStart:     PropTypes.func,
  onSearch:          PropTypes.func,
  onSearchFailed:    PropTypes.func,
  onValidation:      PropTypes.func,
  onClear:           PropTypes.func,
  prescription:      PropTypes.string,
  zipCode:           PropTypes.string
}
